import SectionContainer from "../../shared/section_container/section_container";
import SubTitle from "../../shared/sub-title/sub-title";
import ContentContainer from "../../shared/grid_content_container/grid_content_container";
import FormRow from "../../shared/form-row/form-row";
import StyleInput from "../../inputs/style-input";
import SSelect from "../../shared/s_select/s_select";
import useTimeList, {
  OperatorType
} from "../../../hooks/use_time_list/use_time_list";
import TitleContainer from "../../shared/title_container/title_container";
import EtcSetting from "./etc_setting/etc_setting";
import WSBottomContainer from "../w_s_bottom_container/w_s_bottom_container";
import Button from "../../globalComponents/Button";
import RecordingTimeHelp from "./recording_time_help";
import RadioContainer from "../../shared/radio_container/radio_container";
import { Day_Type, Work_Type } from "../../../generated/graphql";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { WS_STATE } from "../../../screens/view-settings/work-schedule-settings/add-update/asonic-working-system.screen";
import { SInput } from "../work_time_tab/choice_w_s/choice_basic_w_s/choice_basic_w_s";
import styled from "styled-components";
import workingTemplateCommuteSettingsApi, {
  CommuteDay,
  WorkingTemplateCommuteSettings
} from "../../../api/working-template-commute-settings-api";
import {
  createCommuteSettingsDraft,
  validateCommuteSettingsDraft,
  WorkingTemplateCommuteSettingsDraft
} from "./commute-settings.model";
import { CommuteRecognitionSettings } from "./commute-recognition-settings";

interface IProps {
  workType: Work_Type;
}

const MultiRowContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const RowContainer = styled.div<{ $indent?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  ${props => props.$indent && `padding-left: 20px;`}
`;

const workingFormRowHeight = "100px";

const DAY_BY_NUMBER: Record<CommuteDay, Day_Type> = {
  1: Day_Type.Sun,
  2: Day_Type.Mon,
  3: Day_Type.Tue,
  4: Day_Type.Wed,
  5: Day_Type.Thr,
  6: Day_Type.Fri,
  7: Day_Type.Sat
};

const NUMBER_BY_DAY: Record<Day_Type, CommuteDay> = {
  [Day_Type.Sun]: 1,
  [Day_Type.Mon]: 2,
  [Day_Type.Tue]: 3,
  [Day_Type.Wed]: 4,
  [Day_Type.Thr]: 5,
  [Day_Type.Fri]: 6,
  [Day_Type.Sat]: 7
};

const HydrationStatus = styled.div<{ $error?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 0 10px 10px;
  color: ${props =>
    props.$error ? props.theme.colors.darkRed : props.theme.colors.darkGrey};

  button {
    min-height: 32px;
  }
`;

function RecordingTime({ workType }: IProps) {
  const wSState = useContext(WS_STATE);
  const { listOfOperator } = useTimeList();
  const [startOperator, setStartOperator] = useState<OperatorType>(
    OperatorType.MINUS
  );
  const [endOperator, setEndOperator] = useState<OperatorType>(
    OperatorType.MINUS
  );
  const [
    checkinStartWorkTimeSetFlagAddMinOperator,
    setCheckinStartWorkTimeSetFlagAddMinOperator
  ] = useState<OperatorType>(OperatorType.PLUS);
  const [hydrationError, setHydrationError] = useState("");
  const [hydrationNotice, setHydrationNotice] = useState("");
  const [hydrationRetry, setHydrationRetry] = useState(0);
  const hydrationRequest = useRef(0);
  const hydrationKey = useRef<string | null>(null);

  const draft = wSState?.commuteRecordInfo ?? createCommuteSettingsDraft();
  const setDraft = wSState?.setCommuteRecordInfo;

  const updateDraft = useCallback(
    <Field extends keyof WorkingTemplateCommuteSettingsDraft>(
      field: Field,
      value: WorkingTemplateCommuteSettingsDraft[Field]
    ) => {
      setDraft?.(current => ({ ...current, [field]: value }));
    },
    [setDraft]
  );

  const handleDayOfWeek = useCallback((newDay: Day_Type) => {
    const day = NUMBER_BY_DAY[newDay];
    setDraft?.(current => ({
      ...current,
      dayOfWeek: current.dayOfWeek.includes(day)
        ? current.dayOfWeek.filter(item => item !== day)
        : [...current.dayOfWeek, day]
    }));
  }, [setDraft]);

  // 시간을 뺀 분을 만들어주기
  const handleMinute = useCallback((minute: number) => {
    return String(Math.abs(minute)).padStart(2, "0");
  }, []);

  useEffect(() => {
    const workingTemplateIdx = wSState?.workingTemplateIdx;
    if (!workingTemplateIdx) {
      hydrationKey.current = null;
      wSState?.setCommuteHydrationStatus("ready");
      setHydrationError("");
      setHydrationNotice("");
      return;
    }
    if (wSState?.commuteSaveStage !== "idle") return;
    const nextHydrationKey = `${workingTemplateIdx}:${hydrationRetry}`;
    if (hydrationKey.current === nextHydrationKey) return;
    hydrationKey.current = nextHydrationKey;

    const request = hydrationRequest.current + 1;
    hydrationRequest.current = request;
    const controller = new AbortController();
    wSState?.setCommuteHydrationStatus("loading");
    setHydrationError("");
    setHydrationNotice("");

    void workingTemplateCommuteSettingsApi
      .get(workingTemplateIdx, controller.signal)
      .then(response => {
        if (hydrationRequest.current !== request) return;
        const settings: WorkingTemplateCommuteSettings = response.data;
        const { workingTemplateIdx: _templateIdx, ...nextDraft } = settings;
        setStartOperator(
          nextDraft.startWorkingMin < 0
            ? OperatorType.MINUS
            : OperatorType.PLUS
        );
        setEndOperator(
          nextDraft.endWorkingMin < 0
            ? OperatorType.MINUS
            : OperatorType.PLUS
        );
        setCheckinStartWorkTimeSetFlagAddMinOperator(
          nextDraft.checkinStartWorkTimeSetFlagAddMin < 0
            ? OperatorType.MINUS
            : OperatorType.PLUS
        );
        setDraft?.(nextDraft);
        wSState?.setCommuteHydrationStatus("ready");
      })
      .catch(error => {
        if (
          controller.signal.aborted ||
          hydrationRequest.current !== request
        ) {
          return;
        }
        if (
          error &&
          typeof error === "object" &&
          "code" in error &&
          error.code === "WORKING_TEMPLATE_COMMUTE_NOT_FOUND"
        ) {
          setHydrationNotice(
            "저장된 출퇴근 설정이 없어 기본값으로 시작합니다."
          );
          wSState?.setCommuteHydrationStatus("ready");
          return;
        }
        setHydrationError(
          error instanceof Error
            ? error.message
            : "출퇴근 설정을 불러오지 못했습니다."
        );
        wSState?.setCommuteHydrationStatus("error");
      });

    return () => controller.abort();
  }, [
    hydrationRetry,
    setDraft,
    wSState?.setCommuteHydrationStatus,
    wSState?.commuteSaveStage,
    wSState?.workingTemplateIdx
  ]);

  const validationErrors = validateCommuteSettingsDraft(draft);

  return (
    <SectionContainer>
      <TitleContainer flex={0}>
        <SubTitle title={"출/퇴근 기록 설정"} />
        <RecordingTimeHelp />
      </TitleContainer>
      {wSState?.commuteHydrationStatus === "loading" && (
        <HydrationStatus role="status">출퇴근 설정을 불러오는 중입니다.</HydrationStatus>
      )}
      {wSState?.commuteHydrationStatus === "ready" && hydrationNotice && (
        <HydrationStatus role="status">{hydrationNotice}</HydrationStatus>
      )}
      {wSState?.commuteHydrationStatus === "error" && (
        <HydrationStatus $error role="alert">
          <span>{hydrationError || "출퇴근 설정을 불러오지 못했습니다."}</span>
          <Button onClick={() => setHydrationRetry(value => value + 1)}>
            다시 불러오기
          </Button>
        </HydrationStatus>
      )}
      <ContentContainer flex={0}>
        <FormRow title="출/퇴근 기록 방식">
          <span>{`출/퇴근 기록 설정 시간에 가능`}</span>
        </FormRow>
        <FormRow title={"출/퇴근 기록 장치"}>
          <RadioContainer>
            <StyleInput
              type="checkbox"
              id="PC"
              minWidth="15px"
              maxWidth="15px"
              name="device"
              checked={draft.pcUse === 1}
              onChange={() => updateDraft("pcUse", draft.pcUse === 1 ? 0 : 1)}
            />
            <label htmlFor="PC">{`PC`}</label>
            <StyleInput
              type="checkbox"
              id="mobileApp"
              minWidth="15px"
              maxWidth="15px"
              name="device"
              checked={draft.appUse === 1}
              onChange={() => updateDraft("appUse", draft.appUse === 1 ? 0 : 1)}
            />
            <label htmlFor="mobileApp">{`모바일 앱`}</label>
          </RadioContainer>
        </FormRow>
        <FormRow title="출근 체크 알림">
          <span>{`정규 출근시간`}</span>

          <SSelect
            name="startOperator"
            id="startOperator"
            minWidth="50px"
            maxWidth="50px"
            value={startOperator}
            onChange={event => {
              const operator = event.currentTarget.value as OperatorType;
              setStartOperator(operator);
              updateDraft(
                "startWorkingMin",
                operator === OperatorType.PLUS
                  ? Math.abs(draft.startWorkingMin)
                  : -Math.abs(draft.startWorkingMin)
              );
            }}
          >
            {listOfOperator.map(item => (
              <option value={item.value} key={`${item.value}_start`}>
                {item.name}
              </option>
            ))}
          </SSelect>
          <SInput
            type="number"
            name="startMin"
            id="startMin"
            value={handleMinute(draft.startWorkingMin)}
            onChange={event => {
              const minute = Number(event.currentTarget.value);
              updateDraft(
                "startWorkingMin",
                startOperator === OperatorType.PLUS ? minute : -minute
              );
            }}
            max={59}
            min={0}
          />
          <label htmlFor="startMin">{`분 부터 알림`}</label>
        </FormRow>
        <FormRow title="퇴근 체크 알림">
          <span>{`정규 퇴근시간`}</span>
          <SSelect
            name="endOperator"
            id="endOperator"
            minWidth="50px"
            maxWidth="50px"
            value={endOperator}
            onChange={event => {
              const operator = event.currentTarget.value as OperatorType;
              setEndOperator(operator);
              updateDraft(
                "endWorkingMin",
                operator === OperatorType.PLUS
                  ? Math.abs(draft.endWorkingMin)
                  : -Math.abs(draft.endWorkingMin)
              );
            }}
          >
            {listOfOperator.map(item => (
              <option value={item.value} key={`${item.value}_end`}>
                {item.name}
              </option>
            ))}
          </SSelect>
          <SInput
            type="number"
            name="endMin"
            id="endMin"
            value={handleMinute(draft.endWorkingMin)}
            onChange={event => {
              const minute = Number(event.currentTarget.value);
              updateDraft(
                "endWorkingMin",
                endOperator === OperatorType.PLUS ? minute : -minute
              );
            }}
            max={59}
            min={0}
          />
          <label htmlFor="endMin">{`분 부터 알림`}</label>
        </FormRow>
        <FormRow title={"연장근무시간 연결 처리"}>
          <RadioContainer>
            <StyleInput
              type="checkbox"
              id="changeLeaveTimeType"
              minWidth="15px"
              maxWidth="15px"
              name="changeLeaveTimeType"
              checked={draft.approvalDataType === 1}
              onChange={() =>
                updateDraft(
                  "approvalDataType",
                  draft.approvalDataType === 1 ? 0 : 1
                )
              }
            />
            <label htmlFor="changeLeaveTimeType">{`퇴근 체크 시간을 연장근무 종료시간으로 변경`}</label>
          </RadioContainer>
        </FormRow>
        <FormRow title="퇴근 미 체크 시 자동 체크" $isLongTitle>
          <RadioContainer>
            <StyleInput
              type="checkbox"
              id="LeavingPlansTimeUnUse"
              minWidth="15px"
              maxWidth="15px"
              name="Is_Check_Auto_Time"
              checked={draft.unCheckoutAutoCheckoutFlag === 0}
              onChange={() => updateDraft("unCheckoutAutoCheckoutFlag", 0)}
            />
            <label htmlFor="LeavingPlansTimeUnUse">{`미사용`}</label>
            <StyleInput
              type="checkbox"
              id="LeavingPlansTimeLeavingPlansTime"
              minWidth="15px"
              maxWidth="15px"
              name="Is_Check_Auto_Time"
              checked={draft.unCheckoutAutoCheckoutFlag === 1}
              onChange={() => updateDraft("unCheckoutAutoCheckoutFlag", 1)}
            />
            <label htmlFor="LeavingPlansTimeLeavingPlansTime">{`퇴근 계획 시간`}</label>
            <StyleInput
              type="checkbox"
              id="LeavingPlansTimeSetTime"
              minWidth="15px"
              maxWidth="15px"
              name="Is_Check_Auto_Time"
              checked={draft.unCheckoutAutoCheckoutFlag === 2}
              onChange={() => updateDraft("unCheckoutAutoCheckoutFlag", 2)}
            />
            {draft.unCheckoutAutoCheckoutFlag === 2 ? (
              <StyleInput
                id="unCheckoutAutoCheckoutTime"
                minWidth="50px"
                maxWidth="50px"
                name="Is_Check_Auto_Time"
                value={draft.unCheckoutAutoCheckoutTime.slice(0, 5)}
                onChange={event => {
                  updateDraft(
                    "unCheckoutAutoCheckoutTime",
                    event.currentTarget.value ?? ""
                  );
                }}
                placeholder="ex) 16:30"
              />
            ) : (
              <label htmlFor="LeavingPlansTimeSetTime">
                {draft.unCheckoutAutoCheckoutTime.slice(0, 5)}
              </label>
            )}
          </RadioContainer>
        </FormRow>
        <FormRow minHeight={workingFormRowHeight} title="출근 체크 시">
          <MultiRowContainer>
            <RowContainer>
              <RadioContainer>
                <StyleInput
                  type="checkbox"
                  id="checkInStartWorkTimeSetFlag"
                  minWidth="15px"
                  maxWidth="15px"
                  name="checkInStartWorkTimeSetFlag"
                  checked={draft.checkInStartWorkTimeSetFlag === 1}
                  onChange={() =>
                    updateDraft(
                      "checkInStartWorkTimeSetFlag",
                      draft.checkInStartWorkTimeSetFlag === 1 ? 0 : 1
                    )
                  }
                  disabled={workType === Work_Type.AutonomousCommutingPolicy}
                />
                <label htmlFor="checkInStartWorkTimeSetFlag">{`출근 계획 시간 변경 (Asonic 근무제 이용 시 적용)`}</label>
              </RadioContainer>
            </RowContainer>
            <RowContainer $indent>
              <span>{`출근 체크 시간`}</span>
              <SSelect
                name="checkinStartWorkTimeSetFlagAddMinOperator"
                id="checkinStartWorkTimeSetFlagAddMinOperator"
                minWidth="50px"
                maxWidth="50px"
                value={checkinStartWorkTimeSetFlagAddMinOperator}
                onChange={event => {
                  const operator = event.currentTarget.value as OperatorType;
                  setCheckinStartWorkTimeSetFlagAddMinOperator(operator);
                  updateDraft(
                    "checkinStartWorkTimeSetFlagAddMin",
                    operator === OperatorType.PLUS
                      ? Math.abs(draft.checkinStartWorkTimeSetFlagAddMin)
                      : -Math.abs(draft.checkinStartWorkTimeSetFlagAddMin)
                  );
                }}
              >
                {listOfOperator.map(item => (
                  <option value={item.value} key={`${item.value}_start`}>
                    {item.name}
                  </option>
                ))}
              </SSelect>
              <SInput
                type="number"
                name="addMinForWorkingTime"
                id="addMinForWorkingTime"
                value={handleMinute(
                  draft.checkinStartWorkTimeSetFlagAddMin
                )}
                onChange={event => {
                  const minute = Number(event.currentTarget.value);
                  updateDraft(
                    "checkinStartWorkTimeSetFlagAddMin",
                    checkinStartWorkTimeSetFlagAddMinOperator ===
                      OperatorType.PLUS
                      ? minute
                      : -minute
                  );
                }}
                min={0}
              />
              <label htmlFor="addMinForWorkingTime">{`(분) 퇴근시간`}</label>
            </RowContainer>
            <RowContainer $indent>
              <RadioContainer>
                <StyleInput
                  type="checkbox"
                  id="checkinStartWorkTimeSetFlagBefore"
                  minWidth="15px"
                  maxWidth="15px"
                  name="checkinStartWorkTimeSetFlagBefore"
                  checked={draft.updateEndWorkTimeOnlyFlag === 1}
                  onChange={() =>
                    updateDraft(
                      "updateEndWorkTimeOnlyFlag",
                      draft.updateEndWorkTimeOnlyFlag === 1 ? 0 : 1
                    )
                  }
                  disabled={workType === Work_Type.AutonomousCommutingPolicy}
                />
                <label htmlFor="checkinStartWorkTimeSetFlagBefore">{`출근 계획 시간보다, 일찍 체크 시, 퇴근 계획 시간만 변경`}</label>
              </RadioContainer>
            </RowContainer>
          </MultiRowContainer>
        </FormRow>
        <FormRow minHeight={workingFormRowHeight} title="퇴근 체크 시">
          <RadioContainer>
            <StyleInput
              type="checkbox"
              id="checkoutEndWorkTimeSetFlag"
              minWidth="15px"
              maxWidth="15px"
              name="checkoutEndWorkTimeSetFlag"
              checked={draft.checkoutEndWorkTimeSetFlag === 1}
              onChange={() =>
                updateDraft(
                  "checkoutEndWorkTimeSetFlag",
                  draft.checkoutEndWorkTimeSetFlag === 1 ? 0 : 1
                )
              }
              disabled={workType === Work_Type.AutonomousCommutingPolicy}
            />
            <label htmlFor="checkoutEndWorkTimeSetFlag">{`퇴근 계획 시간 변경 (Asonic 근무제 이용 시 적용)`}</label>
          </RadioContainer>
        </FormRow>
        <CommuteRecognitionSettings
          value={draft}
          errors={validationErrors}
          onChange={(field, value) => updateDraft(field, value)}
        />
      </ContentContainer>
      <EtcSetting
        dayOfWeek={draft.dayOfWeek.map(day => DAY_BY_NUMBER[day])}
        handleDayOfWeek={handleDayOfWeek}
      />
      {wSState?.commuteSaveStage === "partial-failure" && (
        <HydrationStatus $error role="alert">
          <span>
            기본정보는 저장됐지만 출퇴근 설정 저장은 완료되지 않았습니다.
          </span>
          <Button onClick={wSState.retryCommuteSettings}>다시 저장</Button>
        </HydrationStatus>
      )}
      <WSBottomContainer>
        <Button
          maxWidth="100%"
          customMinWidth="100%"
          disabled={
            !wSState?.workingTemplateIdx ||
            wSState.commuteHydrationStatus !== "ready" ||
            wSState.commuteSaveStage === "basic-saving" ||
            wSState.commuteSaveStage === "commute-saving"
          }
          onClick={() => {
            wSState?.handleIsSave(true);
          }}
        >
          {`저장`}
        </Button>
      </WSBottomContainer>
    </SectionContainer>
  );
}

export default RecordingTime;
